import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiData from "../../../services/useApiData.js";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Autocomplete,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Switch,
    TextField,
} from "@mui/material";
import PostData from "../../../services/PostData";
import GetData from "../../../services/GetData.js";
import SnackbarEx from "../../Helpers/SnackbarEx.jsx";
import CatalogPageShell from "../../Products/CatalogPageShell.jsx";
import {
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../../Products/catalogGridStyles.js";
import SingleListingEbay from "./SingleListingEbay.jsx";
import NewEbayVarListing from "./NewEbayVarListing.jsx";
import { EBAY_ASPECT_VALUE_MAX, EBAY_TITLE_MAX } from "./ebayFieldLimits.js";
import "../uploadProducts.css";

export default function NewEbayListing() {
    const navigate = useNavigate();
    const [variants, setVariants] = useState(false);
    const [varItemTitle, setVarItemTitle] = useState("");
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false,
    });

    const {
        data: ebayCategories,
        isLoading: loadCategories,
    } = useApiData("api/broylesllc/get-ebay-categories");
    const {
        data: ebayShipping,
        isLoading: ebayShippingLoad,
    } = useApiData("api/broylesllc/get-ebay-fulfill-policies");
    const { data: ebayDescripts } = useApiData(
        "api/broylesllc/get-ebay-descriptions"
    );

    const [ebaySubCategories, setEbaySubCategories] = useState([]);
    const [submitLoad, setSubmitLoad] = useState(false);
    const formFields = [
        { name: "title", type: "text", initialValue: "", atField: "Listing Field" },
        { name: "upc", type: "text", initialValue: "", atField: "UPC" },
        { name: "brand", type: "text", initialValue: "", atField: "Brand API" },
        { name: "size", type: "text", initialValue: "", atField: "BBsktSize" },
        { name: "color / style", type: "text", initialValue: "", atField: "BBsktColor/Style" },
        { name: "weight", type: "text", initialValue: "", atField: "Weight (lbs)" },
        { name: "height", type: "text", initialValue: "", atField: "Height (in)" },
        { name: "depth", type: "text", initialValue: "", atField: "Depth (in)" },
        { name: "width", type: "text", initialValue: "", atField: "Width (in)" },
        { name: "price", type: "number", initialValue: "", atField: "eBay Price" },
        { name: "images", type: "text", initialValue: [], atField: "Hero Photo" },
    ];

    const initalState = formFields.reduce(
        (acc, next) => ({ ...acc, [next.name]: next.initialValue }),
        {}
    );
    const [formData, setFormData] = useState([initalState]);
    const [attributeInput, setAttributeInput] = useState("");
    const [productAttributes, setProductAttributes] = useState([]);

    const handleAddAttribute = (event) => {
        if (event.key === "Enter" && attributeInput.trim() !== "") {
            event.preventDefault();
            if (!productAttributes.includes(attributeInput.trim())) {
                setProductAttributes([...productAttributes, attributeInput.trim()]);
            }
            setAttributeInput("");
        }
    };

    const handleDeleteAttribute = (chipToDelete) => () => {
        setProductAttributes((chips) => chips.filter((chip) => chip !== chipToDelete));
    };

    const handleAddVariant = () => {
        setFormData((prev) => [...prev, initalState]);
    };

    const handleRemoveVariant = (indexToRemove) => {
        setFormData((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleUpdateVariant = (data, index) => {
        setFormData((prev) => {
            const next = [...prev];
            next[index] = data;
            return next;
        });
    };

    const ebayCategoryData = [
        { name: "category", type: "text", initialValue: "" },
        { name: "subCategory", type: "text", initialValue: "" },
        { name: "description", type: "text", initialValue: "" },
        {
            name: "aspects",
            type: "array",
            initialValue: [{ id: "aspects", aspect: "", value: "" }],
        },
    ];

    const [ebayAspects, setEbayAspects] = useState([]);
    const initalEbayState = ebayCategoryData.reduce(
        (acc, next) => ({ ...acc, [next.name]: next.initialValue }),
        {}
    );
    const [initEbayCategories, setInitEbayCategories] = useState(initalEbayState);
    const [selectedAspectValues, setSelectedAspectValues] = useState(() => {
        const initialValues = {};
        if (initalEbayState?.aspects) {
            initalEbayState.aspects.forEach((aspect) => {
                initialValues[aspect.id] = [{ aspect: "", value: "" }];
            });
        }
        return initialValues;
    });

    const handleAddItem = (aspectId) => {
        setSelectedAspectValues((prev) => ({
            ...prev,
            [aspectId]: [...(prev[aspectId] || []), { aspect: "", value: "" }],
        }));
    };

    const handleRemoveItem = (aspectId, itemIndex) => {
        setSelectedAspectValues((prev) => ({
            ...prev,
            [aspectId]: prev[aspectId].filter((_, idx) => idx !== itemIndex),
        }));
    };

    const [isFormValid, setIsFormValid] = useState(false);

    useEffect(() => {
        const { title, brand, price } = formData[0] || {};
        const { category, subCategory } = initEbayCategories;
        setIsFormValid(Boolean(title && brand && price && category && subCategory));
    }, [formData, initEbayCategories]);

    const handleSubmit = async () => {
        setSubmitLoad(true);
        const submitData = {
            productInfo: formData,
            ebayInfo: initEbayCategories,
            aspectObject: selectedAspectValues,
            variantUpload: variants,
            variantAspects: productAttributes,
            ...(varItemTitle && { varTitle: varItemTitle }),
        };
        const { success: ebaySuccess, error: ebayCreateError } = await PostData(
            submitData,
            "broylesllc/create-ebay-sku",
            null
        );
        if (ebaySuccess) {
            setSubmitLoad(false);
            setOpenTestSnack({
                color: "success",
                message: "Item added to eBay.",
                open: true,
            });
            setTimeout(() => navigate(-1), 1000);
        } else {
            setSubmitLoad(false);
            setOpenTestSnack({
                color: "error",
                message: ebayCreateError?.response?.data,
                open: true,
            });
        }
    };

    const handleEbaySetupData = async (d, index) => {
        if (index > 0) return;
        const chosenRec = ebayCategories?.find(
            (v) => v.name === d?.[0]?.["eBay Category"]
        );
        const chosenShipRec = ebayShipping?.fulfillmentPolicies?.find(
            (v) => v.name === d?.[1]?.["eBay Shipping Policy"]
        );
        setInitEbayCategories({
            ...initEbayCategories,
            category: chosenRec?.name,
            categoryId: chosenRec?.id,
            shipPolicyName: chosenShipRec?.name,
            shipPolicyId: chosenShipRec?.fulfillmentPolicyId,
        });
        if (chosenRec?.id) {
            const { getData } = await GetData(
                `broylesllc/get-ebay-sub-categories/${chosenRec.id}`
            );
            if (getData) setEbaySubCategories(getData);
        }
    };

    return (
        <CatalogPageShell
            title="New eBay Product"
            subtitle="Create a single or variant listing from a product SKU."
        >
            <div className="upload-stack">
                <div className="upload-toolbar">
                    <Button
                        size="small"
                        startIcon={<ArrowBackOutlinedIcon />}
                        onClick={() => navigate(-1)}
                        sx={catalogQuietButtonSx}
                        variant="outlined"
                    >
                        Back
                    </Button>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={variants}
                                onChange={() => setVariants((v) => !v)}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#0d9488",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                            backgroundColor: "#0d9488",
                                        },
                                }}
                            />
                        }
                        label="Variant product"
                        labelPlacement="start"
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#1c2430",
                            },
                        }}
                    />
                </div>

                <div className="upload-form-scroll">
                    {variants ? (
                        <div className="mb-3 d-flex flex-wrap align-items-center gap-2 justify-content-end">
                            {productAttributes.map((data) => (
                                <Chip
                                    key={data}
                                    label={data}
                                    onDelete={handleDeleteAttribute(data)}
                                    sx={{
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                        backgroundColor: "#f1f5f9",
                                    }}
                                />
                            ))}
                            <TextField
                                size="small"
                                required
                                variant="outlined"
                                helperText="Press enter to set variant"
                                label="Variant type(s)"
                                placeholder="Pattern, Size …"
                                value={attributeInput}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setAttributeInput(
                                        (
                                            val.charAt(0).toUpperCase() +
                                            val.slice(1).replace(/([A-Z])/g, " $1")
                                        ).trim()
                                    );
                                }}
                                onKeyDown={handleAddAttribute}
                                sx={{
                                    ...catalogSearchFieldSx,
                                    minWidth: 220,
                                }}
                            />
                        </div>
                    ) : null}

                    {!variants ? (
                        <SingleListingEbay
                            submitLoad={submitLoad}
                            updateFormData={(d) => setFormData(d)}
                            ebaySetupData={(d) => handleEbaySetupData(d)}
                        />
                    ) : (
                        <>
                            <p className="upload-section-label">Master Item Title</p>
                            <TextField
                                value={varItemTitle}
                                error={varItemTitle?.length > EBAY_TITLE_MAX}
                                helperText={
                                    varItemTitle?.length > EBAY_TITLE_MAX
                                        ? `Title is over eBay's ${EBAY_TITLE_MAX} character limit`
                                        : null
                                }
                                inputProps={{ maxLength: EBAY_TITLE_MAX }}
                                size="small"
                                onChange={(e) =>
                                    setVarItemTitle(
                                        e.target.value.slice(0, EBAY_TITLE_MAX)
                                    )
                                }
                                sx={{
                                    ...catalogSearchFieldSx,
                                    width: { xs: "100%", sm: "50%" },
                                    mb: 2,
                                }}
                            />
                            {formData.map((variantData, index) => (
                                <NewEbayVarListing
                                    key={index}
                                    ebaySetupData={(d) => handleEbaySetupData(d, index)}
                                    varAttributes={productAttributes}
                                    submitLoad={submitLoad}
                                    formData={variantData}
                                    updateFormData={(d) => handleUpdateVariant(d, index)}
                                />
                            ))}
                            <div className="d-flex justify-content-end gap-2 mb-3">
                                {formData.length > 1 ? (
                                    <Button
                                        startIcon={<DeleteIcon />}
                                        variant="outlined"
                                        sx={catalogDangerButtonSx}
                                        onClick={() =>
                                            handleRemoveVariant(formData.length - 1)
                                        }
                                    >
                                        Remove Variant
                                    </Button>
                                ) : null}
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    sx={catalogPrimaryButtonSx}
                                    onClick={handleAddVariant}
                                >
                                    Add Variant
                                </Button>
                            </div>
                        </>
                    )}

                    <section
                        className="upload-panel"
                        style={{ marginTop: variants ? 8 : 20 }}
                    >
                        <div className="upload-panel-header">
                            <h3 className="upload-panel-title">eBay Setup</h3>
                        </div>
                        <div className="upload-panel-body">
                            <div className="row g-2 m-0">
                                <div className="col-12 col-md-6">
                                    <Autocomplete
                                        size="small"
                                        disabled={submitLoad}
                                        options={
                                            ebayShipping?.fulfillmentPolicies?.map(
                                                (policy) => policy.name
                                            ) || []
                                        }
                                        loading={ebayShippingLoad}
                                        value={initEbayCategories.shipPolicyName || null}
                                        onChange={(_e, newValue) => {
                                            const chosenRec =
                                                ebayShipping?.fulfillmentPolicies?.find(
                                                    (v) => v.name === newValue
                                                );
                                            setInitEbayCategories({
                                                ...initEbayCategories,
                                                shipPolicyName: newValue,
                                                shipPolicyId:
                                                    chosenRec?.fulfillmentPolicyId,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Shipping Policy"
                                                sx={{
                                                    ...catalogSearchFieldSx,
                                                    width: "100%",
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <Autocomplete
                                        size="small"
                                        disabled={submitLoad}
                                        options={ebayDescripts || []}
                                        getOptionLabel={(o) => o?.templateName || ""}
                                        value={initEbayCategories.description || null}
                                        onChange={(_e, newValue) => {
                                            setInitEbayCategories({
                                                ...initEbayCategories,
                                                description: newValue,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Listing Description"
                                                sx={{
                                                    ...catalogSearchFieldSx,
                                                    width: "100%",
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <Autocomplete
                                        size="small"
                                        disabled={submitLoad}
                                        options={
                                            ebayCategories?.map((category) => category.name) ||
                                            []
                                        }
                                        loading={loadCategories}
                                        value={initEbayCategories.category || null}
                                        onChange={async (_e, newValue, reason) => {
                                            const chosenRec = ebayCategories?.find(
                                                (v) => v.name === newValue
                                            );
                                            setInitEbayCategories({
                                                ...initEbayCategories,
                                                category: newValue,
                                                categoryId: chosenRec?.id,
                                            });
                                            if (reason !== "clear" && chosenRec?.id) {
                                                const { getData } = await GetData(
                                                    `broylesllc/get-ebay-sub-categories/${chosenRec.id}`
                                                );
                                                if (getData) setEbaySubCategories(getData);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Category"
                                                sx={{
                                                    ...catalogSearchFieldSx,
                                                    width: "100%",
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadCategories ? (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={18}
                                                                />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <Autocomplete
                                        size="small"
                                        disabled={submitLoad}
                                        options={
                                            ebaySubCategories?.map(
                                                (category) => category?.name
                                            ) || []
                                        }
                                        loading={loadCategories}
                                        value={initEbayCategories.subCategory || null}
                                        onChange={async (_e, newValue, reason) => {
                                            const chosenRec = ebaySubCategories.find(
                                                (v) => v.name === newValue
                                            );
                                            setInitEbayCategories({
                                                ...initEbayCategories,
                                                subCategory: newValue,
                                                subCategoryId: chosenRec?.id,
                                            });
                                            if (reason !== "clear" && chosenRec?.id) {
                                                const { getData } = await GetData(
                                                    `broylesllc/get-ebay-assets/${chosenRec.id}`
                                                );
                                                if (getData) setEbayAspects(getData);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Sub-category"
                                                sx={{
                                                    ...catalogSearchFieldSx,
                                                    width: "100%",
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="mt-3">
                                <p className="upload-section-label">Aspects</p>
                                <div className="d-flex flex-column gap-2">
                                    {initEbayCategories.aspects?.map((aspect, aid) => (
                                        <div key={aid} className="mb-2">
                                            <p className="upload-section-label mb-2">
                                                {aspect.name}
                                            </p>
                                            {(selectedAspectValues[aspect.id] || []).map(
                                                (item, itemIndex) => (
                                                    <div
                                                        key={itemIndex}
                                                        className="d-flex align-items-center gap-2 mb-2"
                                                    >
                                                        <Autocomplete
                                                            size="small"
                                                            disabled={submitLoad}
                                                            value={item?.aspect || ""}
                                                            options={
                                                                ebayAspects?.map(
                                                                    (a) => a?.name
                                                                ) || []
                                                            }
                                                            onChange={(_event, newValue) => {
                                                                setSelectedAspectValues(
                                                                    (prev) => {
                                                                        const updated = [
                                                                            ...(prev[
                                                                                aspect.id
                                                                            ] || []),
                                                                        ];
                                                                        updated[itemIndex] = {
                                                                            ...updated[
                                                                                itemIndex
                                                                            ],
                                                                            aspect: newValue,
                                                                        };
                                                                        return {
                                                                            ...prev,
                                                                            [aspect.id]:
                                                                                updated,
                                                                        };
                                                                    }
                                                                );
                                                            }}
                                                            sx={{ flexGrow: 1 }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder="Select aspect"
                                                                    sx={catalogSearchFieldSx}
                                                                />
                                                            )}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            disabled={submitLoad}
                                                            placeholder="Free text"
                                                            inputProps={{
                                                                maxLength:
                                                                    EBAY_ASPECT_VALUE_MAX,
                                                            }}
                                                            helperText={`Max ${EBAY_ASPECT_VALUE_MAX} characters`}
                                                            onChange={(e) => {
                                                                const rawValue =
                                                                    e.target.value.slice(
                                                                        0,
                                                                        EBAY_ASPECT_VALUE_MAX
                                                                    );
                                                                const capitalizedValue =
                                                                    rawValue.charAt(0).toUpperCase() +
                                                                    rawValue.slice(1);
                                                                setSelectedAspectValues(
                                                                    (prev) => {
                                                                        const updated = [
                                                                            ...(prev[
                                                                                aspect.id
                                                                            ] || []),
                                                                        ];
                                                                        updated[itemIndex] = {
                                                                            ...updated[
                                                                                itemIndex
                                                                            ],
                                                                            value: capitalizedValue,
                                                                        };
                                                                        return {
                                                                            ...prev,
                                                                            [aspect.id]:
                                                                                updated,
                                                                        };
                                                                    }
                                                                );
                                                            }}
                                                            sx={{
                                                                ...catalogSearchFieldSx,
                                                                flexGrow: 1,
                                                            }}
                                                        />
                                                        <IconButton
                                                            aria-label="delete"
                                                            size="small"
                                                            onClick={() =>
                                                                handleRemoveItem(
                                                                    aspect.id,
                                                                    itemIndex
                                                                )
                                                            }
                                                            sx={{ color: "#e03e2d" }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            aria-label="add"
                                                            size="small"
                                                            onClick={() =>
                                                                handleAddItem(aspect.id)
                                                            }
                                                            sx={{ color: "#0d9488" }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <div className="upload-footer">
                    <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        sx={catalogQuietButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid || submitLoad}
                        onClick={handleSubmit}
                        startIcon={
                            submitLoad ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Publish to eBay
                    </Button>
                </div>
            </div>

            {openTestSnack.open && (
                <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() =>
                        setOpenTestSnack({ color: "", message: "", open: false })
                    }
                />
            )}
        </CatalogPageShell>
    );
}
